export const MAX_IMAGE_SIZE_BYTES = 500 * 1024; // 500 KB
export const MAX_IMAGE_SIZE_LABEL = "500 KB";

const getApiBaseUrl = () =>
  process.env.REACT_APP_Environment === "development"
    ? process.env.REACT_APP_API_URL || ""
    : process.env.REACT_APP_API_URL_Production || "";

/**
 * Validates that a file is an image and within the 500 KB size limit.
 * @returns {{ valid: boolean, message?: string }}
 */
export const validateImageFile = (file, options = {}) => {
  const { requireImageType = true, includeFileName = true } = options;
  if (!file) {
    return { valid: false, message: "No file selected" };
  }

  const isImage =
    (file.type && file.type.startsWith("image/")) ||
    /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(file.name || "");

  if (requireImageType && !isImage) {
    const prefix = includeFileName && file.name ? `${file.name}: ` : "";
    return { valid: false, message: `${prefix}not an image file` };
  }

  if (isImage && file.size > MAX_IMAGE_SIZE_BYTES) {
    const prefix = includeFileName && file.name ? `${file.name}: ` : "";
    return {
      valid: false,
      message: `${prefix}image must be ${MAX_IMAGE_SIZE_LABEL} or smaller`,
    };
  }

  return { valid: true };
};

/** Relative path for API payload (strips API base URL if present). */
export const toRelativeMediaPath = (pathOrUrl) => {
  if (!pathOrUrl) return "";
  const value = String(pathOrUrl).trim();
  if (value.startsWith("blob:") || value.startsWith("data:")) return value;
  const base = getApiBaseUrl().replace(/\/$/, "");
  if (base && value.startsWith(base)) {
    return value.slice(base.length).replace(/^\/+/, "");
  }
  if (value.startsWith("http://") || value.startsWith("https://")) {
    try {
      const url = new URL(value);
      return url.pathname.replace(/^\/+/, "");
    } catch {
      return value;
    }
  }
  return value.replace(/^\/+/, "");
};

/** Full URL for preview (prepends API base when path is relative). */
export const toDisplayMediaUrl = (pathOrUrl) => {
  if (!pathOrUrl) return "";
  const value = String(pathOrUrl).trim();
  if (
    value.startsWith("blob:") ||
    value.startsWith("data:") ||
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    return value;
  }
  const base = getApiBaseUrl().replace(/\/$/, "");
  const path = value.replace(/^\/+/, "");
  return base ? `${base}/${path}` : `/${path}`;
};

export const pathsMatchForSelection = (a, b) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return toRelativeMediaPath(a) === toRelativeMediaPath(b);
};

/** Upload image via media API (registers in company gallery). Returns relative path. */
export const uploadCompanyMediaFile = async (
  axiosInstance,
  APIs,
  compID,
  imageFile,
  mediaCategory = "Product"
) => {
  const validation = validateImageFile(imageFile);
  if (!validation.valid) {
    throw new Error(validation.message || `Image must be ${MAX_IMAGE_SIZE_LABEL} or smaller`);
  }

  const formData = new FormData();
  formData.append("ImageFile", imageFile);
  const response = await axiosInstance.post(APIs.POST_UPLOAD_COMPANY_MEDIA, formData, {
    params: { CompID: compID, MediaCategory: mediaCategory },
    headers: { "Content-Type": "multipart/form-data" },
  });
  if (response.data?.status !== true) {
    throw new Error(response.data?.message || "Image upload failed");
  }
  const data = response.data?.data || {};
  const filePath = data.filePath || data.FilePath || "";
  return toRelativeMediaPath(filePath);
};

/** Bulk upload images via media API. Returns API response data with uploaded/failed lists. */
export const uploadCompanyMediaFilesBulk = async (
  axiosInstance,
  APIs,
  compID,
  imageFiles,
  mediaCategory = "Product"
) => {
  const files = Array.from(imageFiles || []);
  for (const file of files) {
    const validation = validateImageFile(file);
    if (!validation.valid) {
      throw new Error(validation.message || `Image must be ${MAX_IMAGE_SIZE_LABEL} or smaller`);
    }
  }

  const formData = new FormData();
  files.forEach((file) => formData.append("ImageFiles", file));
  const response = await axiosInstance.post(APIs.POST_UPLOAD_COMPANY_MEDIA_BULK, formData, {
    params: { CompID: compID, MediaCategory: mediaCategory },
    headers: { "Content-Type": "multipart/form-data" },
  });
  if (response.data?.status !== true) {
    throw new Error(response.data?.message || "Bulk image upload failed");
  }
  return response.data?.data || {};
};
